/**
 * auditVocab.ts — the per-service audit ACTION and TARGET-TYPE vocabularies
 * (UX Wave 3c, audit tracer).
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: UX Wave 3c (Insights +
 * audit tracer, 2026-07-21).
 *
 * WHY a local mirror: each owning service's `GET /admin/audit` read validates
 * its `action`/`targetType` filter comma-lists STRICTLY against its OWN slice
 * of the shared G2 taxonomy (one unknown value is a boundary 400, never a
 * silently-empty page). The tracer's filter bar therefore has to know exactly
 * which values belong to which service: it offers the UNION to the admin, but
 * sends each service ONLY the intersection with that service's vocabulary —
 * and skips a service entirely when the intersection is empty (a filter no
 * service value can match returns nothing by definition; asking would 400).
 * Mirrors the services' zod enums (orders/identity `src/audit/`, clinic
 * `src/audit/audit.schemas.ts`) — [CONTRACTS GAP]: promote alongside the
 * shared audit shapes once they land in @thegpservice/gpsclinics-contracts;
 * until then keep this file in lock-step with the service enums.
 */

/** The three owning services — the tracer's "origin service" dimension. */
export type AuditSource = 'orders' | 'identity' | 'clinic';

export const AUDIT_SOURCES: readonly AuditSource[] = ['orders', 'identity', 'clinic'];

/** Human label for a source ("origin service" in the expanded row detail). */
export const AUDIT_SOURCE_LABELS: Record<AuditSource, string> = {
  orders: 'Orders service',
  identity: 'Identity service',
  clinic: 'Clinic service',
};

/** Each service's action vocabulary — exact strings, matching its zod enum. */
export const AUDIT_ACTIONS: Record<AuditSource, readonly string[]> = {
  orders: [
    'order_approved',
    'order_rejected',
    'prescription_issued',
    'order_preapproved',
    'payment_refunded',
    'compensation_updated',
    // ⚠️ THE ONE DURABLE RECORD OF A PAID ORDER THE PHARMACY NEVER RECEIVED,
    // and until 2026-09-11 it was unfilterable here (incident 2026-09-09).
    //
    // orders-service has written this row since the fail-closed identity check
    // shipped: `sendDispenseRequest` refuses a dispensing request it cannot put
    // a name and a date of birth on, and `recordBlockedSend` records the refusal
    // with a PHI-free reason enum. The action has been in that service's audit
    // vocabulary all along, so its own `GET /admin/audit` accepts it as a filter
    // — this MIRROR was what omitted it, and the tracer sends each service only
    // the intersection with its listed vocabulary. The effect: an admin who knew
    // exactly what had gone wrong, and knew the row existed, still could not ask
    // the tracer for it. The only remaining trace was a Cloud Logging line at
    // DEFAULT severity that no alert policy in the project can match.
    //
    // A patient sat paid and undispatched for two days. This is one string.
    'pharmacy_send_blocked',
    // Its sibling in the same class and the same lane — the auto-issue refused
    // for want of an identifiable PRESCRIBER. Added alongside because the two
    // are triaged by the same person asking the same question ("why is this paid
    // order not moving?"), and leaving one filterable and the other not would
    // reintroduce the gap the moment the failure came from the other side.
    'prescription_issue_blocked',
    // Edit plan (CTO 2026-09-24) — the PATIENT changed the plan on a case whose
    // card form was already open (`awaiting_payment`): a re-quoted price for the
    // dormant subscription and the month-1 hold, and, on a medicine change, the
    // WITHDRAWAL of the case's safety acknowledgement. Added in the same batch as
    // the action, so the one row that explains why a case no longer carries an
    // acknowledgement is filterable from day one — not caught up later, as
    // `pharmacy_send_blocked` and `recommendation_issued` both had to be.
    // ⚠️ orders' audit filter is a STRICT enum: this value 400s against an orders
    // build that predates it, so orders (`feat/checkout-replan-awaiting-payment`)
    // ships BEFORE this portal — the release order Edit plan already requires.
    // (`prescription_supplies_backfilled` belongs to the supplies branch and goes
    // in with it, not here.)
    'checkout_plan_changed',
    // Try a different card (2026-09-25) — the PATIENT set up a new card on an
    // order whose month-1 hold the bank DECLINED, rotating its card session
    // (context `{ cardAttempt }`, the `:c{n}` in orders' hold key). The first row
    // to look at for "was I charged twice?" and for a case stopped at the
    // cards-per-order cap. Same release rule as the line above: orders'
    // strict filter 400s it until orders `feat/checkout-retry-card-after-decline`
    // is out, so orders ships BEFORE this portal.
    'checkout_card_retried',
    // 2026-09-24 — the one-off release backfill of prescription SUPPLIES. A
    // pending (issued, not yet dispensed) prescription had its empty supplies
    // section filled with the catalogue's pen supply (and a sharps bin for a
    // first-time patient), signed by the service's SYSTEM principal. It is the
    // ONE write to an issued prescription after issue — medicine lines, notes
    // and prescriber are never touched — so it is exactly the row a reviewer
    // asks the tracer for ("who changed this signed document, and when?").
    // ADMIN-only on orders' audit read, and target type `prescription`.
    'prescription_supplies_backfilled',
    // G2 (2026-09-25, orders `fix/release-superseded-hold`, behind orders'
    // HOLD_RELEASE_ON_CHANGE_ENABLED) — orders CANCELLED a month-1 hold priced for
    // a plan the case had LEFT (an Edit plan race whose own release failed), once
    // the new plan's hold was recorded. Target `order_case`, context
    // `{ amountPence, planRevision }`, signed by orders' SYSTEM principal. The row
    // that answers "why did my bank show two holds, and when did the old one
    // go?". ADMIN-only on orders' audit read.
    // ⚠️ Same release rule as `checkout_plan_changed`: orders' strict filter 400s
    // this value until that orders build is out, so orders ships BEFORE this
    // portal.
    'hold_released_on_change',
  ],
  identity: [
    'invite_created',
    'invite_resent',
    'invite_revoked',
    'user_deleted',
    'reset_link_issued',
    'clinician_record_updated',
  ],
  clinic: [
    'callback_status_changed',
    'callback_called',
    'callback_closed',
    'assessment_handover_sent',
    'deal_created',
    'deal_stage_changed',
    'deal_handover',
    // Repeat-callback attach: same-stage relink of the ticket on the one deal.
    'deal_callback_linked',
    // §9 three-attempt workflow — one governance row per logged call attempt.
    'deal_call_attempt',
    // Wave 4 — the clinician issued/replaced a medication recommendation set.
    // Caught up 2026-08-26: the enum shipped in the service long before this
    // mirror, so the tracer could not FILTER for approvals — exactly the rows
    // the first live clinical day needed to find.
    'recommendation_issued',
    // UX Wave 9 §4 — a STAFF submit of an assessment (the drawer's
    // single-page form): the "forms they completed" row on My activity.
    'assessment_submitted',
    // Clinician hard-stop safety net (2026-08-06) + its recover undo.
    'assessment_hardstop_closed',
    'assessment_hardstop_revised',
    'deal_recovered',
    // Clinician → admin handoff (2026-08-07).
    'deal_pending_admin_review',
    // ── "DID A CLINICIAN EVER RAISE A CONCERN ABOUT THIS PATIENT?" ─────────
    // A named clinician stopped and formed a view, which is a governance
    // question a reviewer asks directly and could not ask here at all — the same
    // gap `recommendation_issued` had on the first live clinical day.
    //
    // ⚠️ ITS SIBLING `weight_evidence_audit_sampled` IS DELIBERATELY NOT HERE.
    // The routine-sample lane is not built: `git grep weight_evidence` across
    // clinic-service's `dev` returns only `_added`, `_removed` and `_requested`,
    // so the value exists in no service's enum — and clinic's audit filter is a
    // STRICT enum, so a mirror entry for it would hand the tracer a checkbox
    // whose only possible outcome is a 400. It goes in WITH that lane (which is
    // waiting on a cadence and sample-size decision), in the same change that
    // makes the action real, so the two questions become separable on the day
    // there are two answers.
    'weight_evidence_requested',
    // Marketing consent (2026-09-11) — a STAFF-recorded withdrawal from the
    // deal drawer ("the patient asked on the phone"), written in the same
    // transaction as the consent ledger row, context `{ channel }`. Staff can
    // only withdraw, so there is deliberately no `…_granted` twin: a grant comes
    // from the person, on the guest lane, which writes no AuditEntry at all.
    'marketing_consent_withdrawn',
    // ── The engagement / unlock lane (clinic-service, 2026-09-23) ──────────
    // Six actions the release adds to clinic's STRICT audit enum
    // (`src/audit/audit.schemas.ts`) — mirrored here in the SAME release so
    // the tracer can filter for them from day one (release validation
    // 2026-09-25: without them an admin could not ask "which guides went to
    // this patient?" or "who revoked these links?"). Each is a live filter
    // clinic accepts: a guide emailed, the unlock link copied for a hand send
    // (⚠️ a FLOOR, never a count), a WhatsApp send logged by hand, every live
    // link revoked, and a guide published or retired in the content library.
    'engagement_content_sent',
    'engagement_link_copied',
    'engagement_whatsapp_logged',
    'engagement_links_revoked',
    'engagement_content_published',
    'engagement_content_retired',
    // ── Returning patients S4.1–S4.2 (clinic #224/#225, mirrored 2026-09-26
    // with the S4.5 portal build) ────────────────────────────────────────────
    // THE GOVERNANCE QUESTION THE HISTORY TAB CREATES: "who opened THIS earlier
    // questionnaire, from which card, and why?" is answered only by
    // `assessment_history_answers_viewed` (target = the questionnaire opened;
    // context = the card, the match basis and the reason CODE). Shipping the
    // logged reveal without the filter would make the log unaskable. The other
    // six are the one-card-per-submission lane: a draft attached to a pre-submit
    // card, a card superseded by a newer questionnaire, a newer submission while
    // this card stayed open, a supersede released (money / late submission /
    // the ADMIN reopen this build wires up), a plan cancelled behind a card, and
    // an address its owner disputed. Clinic ships first (it accepts them from
    // #224/#225 on); each is a live filter on clinic's strict enum.
    'deal_assessment_attached',
    'deal_superseded',
    'deal_newer_submission',
    'deal_supersede_released',
    'deal_plan_cancelled',
    'assessment_history_answers_viewed',
    'contact_email_disputed',
  ],
};

/*
 * ⚠️ THIS MIRROR IS STILL BEHIND THE SERVICE ENUMS (noted 2026-09-07, NOT
 * fixed here). clinic-service's `auditContextSchemas`/`auditAction` also carry
 * `photo_request_sent`, `weight_evidence_added`, `weight_evidence_removed`,
 * `deal_marked_test`, `deal_marked_duplicate`, `deal_duplicate_absorbed`,
 * `assessment_note_added`/`_edited`, `assessment_plan_link_resent`/`_copied`
 * and more, none of which the tracer can filter for. Each is a real governance
 * question an admin cannot currently ask. Left alone deliberately: catching the
 * whole vocabulary up is its own slice with its own verification (every added
 * value is a live filter that must round-trip against three services' strict
 * boundary enums), and folding it into an unrelated feature branch is how a
 * one-line addition turns into an untested ten.
 */

/** Each service's target-type vocabulary — exact strings, matching its zod enum. */
export const AUDIT_TARGET_TYPES: Record<AuditSource, readonly string[]> = {
  orders: ['order_case', 'prescription', 'compensation'],
  identity: ['user', 'invite', 'clinician_record'],
  // 'content' — the engagement library's publish/retire rows target the guide
  // itself (clinic `auditTargetType`, 2026-09-23).
  clinic: ['callback', 'assessment', 'deal', 'content'],
};

/** The cross-service action UNION, in service order — the filter bar's option list. */
export const AUDIT_ACTION_UNION: readonly string[] = AUDIT_SOURCES.flatMap((s) => AUDIT_ACTIONS[s]);

/** The cross-service target-type UNION — the target-type select's option list. */
export const AUDIT_TARGET_TYPE_UNION: readonly string[] = AUDIT_SOURCES.flatMap((s) => AUDIT_TARGET_TYPES[s]);
